/**
 * QuickFill Revit Sync Hook
 *
 * Provides functionality to sync QuickFill data with Revit families.
 */

import { useCallback, useState, useRef } from 'react';
import {
  saveRowsToRevit,
  saveChangesToRevit,
  saveRollbackToRevit as saveRollbackChangesToRevit,
  saveBulkEditToRevit,
  saveCellsToRevit as saveCellsToRevitService,
} from '../services/quickFillRevitService';
import type {
  BulkUpdateResult,
  PropertyMapping,
  QuickFillBindingScope,
  QuickFillCellUpdate,
  QuickFillSyncProgress,
} from '../services/quickFillRevitService';
import type { QuickFillItem } from '../types';

/**
 * Hook for syncing QuickFill data with Revit
 */
export function useQuickFillRevitSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<BulkUpdateResult | null>(null);
  const [progress, setProgress] = useState<QuickFillSyncProgress | null>(null);

  // Track original data for change detection
  const originalDataRef = useRef<QuickFillItem[]>([]);

  /**
   * Set the original data snapshot for change detection
   */
  const setOriginalData = useCallback((data: QuickFillItem[]) => {
    originalDataRef.current = JSON.parse(JSON.stringify(data));
  }, []);

  /**
   * Save all rows to Revit
   */
  const saveAllToRevit = useCallback(
    async (
      items: QuickFillItem[],
      properties: PropertyMapping[]
    ): Promise<BulkUpdateResult> => {
      setIsSyncing(true);
      setProgress({
        phase: 'preparing',
        message: 'Preparing Quick Fill changes for Revit',
        current: 0,
        total: 0,
      });
      try {
        const hasSnapshot = originalDataRef.current.length > 0;
        const result = hasSnapshot
          ? await saveChangesToRevit(items, originalDataRef.current, properties, { onProgress: setProgress })
          : await saveRowsToRevit(items, properties, undefined, { onProgress: setProgress });
        setLastResult(result);
        originalDataRef.current = JSON.parse(JSON.stringify(items));
        return result;
      } catch (error) {
        console.error('[useQuickFillRevitSync] saveAllToRevit error:', error);
        throw error;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Save specific rows to Revit
   */
  const saveSelectedToRevit = useCallback(
    async (
      items: QuickFillItem[],
      properties: PropertyMapping[],
      rowIndices: number[]
    ): Promise<BulkUpdateResult> => {
      setIsSyncing(true);
      setProgress({
        phase: 'preparing',
        message: 'Preparing Quick Fill changes for Revit',
        current: 0,
        total: 0,
      });
      try {
        const result = await saveRowsToRevit(items, properties, rowIndices, { onProgress: setProgress });
        setLastResult(result);
        return result;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Save bulk edit to Revit
   */
  const saveBulkEdit = useCallback(
    async (
      items: QuickFillItem[],
      rowIndices: number[],
      field: string,
      value: string,
      revitParameter: string,
      bindingScope?: QuickFillBindingScope
    ): Promise<BulkUpdateResult> => {
      setIsSyncing(true);
      setProgress({
        phase: 'preparing',
        message: 'Preparing Quick Fill changes for Revit',
        current: 0,
        total: 0,
      });
      try {
        const result = await saveBulkEditToRevit(
          items,
          rowIndices,
          field,
          value,
          revitParameter,
          { onProgress: setProgress },
          bindingScope
        );
        setLastResult(result);
        return result;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Save explicit changed cells to Revit.
   */
  const saveCellsToRevit = useCallback(
    async (
      items: QuickFillItem[],
      updates: QuickFillCellUpdate[]
    ): Promise<BulkUpdateResult> => {
      setIsSyncing(true);
      setProgress({
        phase: 'preparing',
        message: 'Preparing Quick Fill changes for Revit',
        current: 0,
        total: 0,
      });
      try {
        const result = await saveCellsToRevitService(items, updates, { onProgress: setProgress });
        setLastResult(result);
        return result;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Save restored QuickFill values back to Revit during cross-session undo.
   */
  const saveRollbackToRevit = useCallback(
    async (
      currentItems: QuickFillItem[],
      restoredItems: QuickFillItem[],
      properties: PropertyMapping[]
    ): Promise<BulkUpdateResult> => {
      setIsSyncing(true);
      setProgress({
        phase: 'preparing',
        message: 'Preparing Quick Fill changes for Revit',
        current: 0,
        total: 0,
      });
      try {
        const result = await saveRollbackChangesToRevit(
          currentItems,
          restoredItems,
          properties,
          { onProgress: setProgress }
        );
        setLastResult(result);
        if (result.failureCount === 0) {
          originalDataRef.current = JSON.parse(JSON.stringify(restoredItems));
        }
        return result;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Reset the last result
   */
  const resetResult = useCallback(() => {
    setLastResult(null);
    setProgress(null);
  }, []);

  return {
    isSyncing,
    lastResult,
    progress,
    saveAllToRevit,
    saveSelectedToRevit,
    saveBulkEdit,
    saveCellsToRevit,
    saveRollbackToRevit,
    setOriginalData,
    resetResult,
  };
}
